import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

/* ---------------------------------------------------------------
   Types
--------------------------------------------------------------- */
interface Benefit {
  title: string;
  icon: React.ReactNode;
}

interface Phase {
  number: number;
  title: string;
  weeks: string;
  activities: number;
  description: string;
  color: string;
  bg: string;
  ring: string;
  text: string;
}

interface ScienceCard {
  text: string;
  icon: React.ReactNode;
}

interface FAQ {
  question: string;
  answer: string;
}

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  toggle: () => void;
}

/* ---------------------------------------------------------------
   Animation variants
--------------------------------------------------------------- */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      delay: i * 0.12,
    },
  }),
};

/* ---------------------------------------------------------------
   Data
--------------------------------------------------------------- */
const benefits: Benefit[] = [
  {
    title: 'Prépare le cerveau aux apprentissages',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path
          d="M24 4C16.268 4 10 10.268 10 18c0 4.42 2.058 8.36 5.27 10.916C16.482 30.058 17 31.48 17 33v1a2 2 0 002 2h10a2 2 0 002-2v-1c0-1.52.518-2.942 1.73-4.084C35.942 26.36 38 22.42 38 18c0-7.732-6.268-14-14-14z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M20 40h8M22 44h4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <path
          d="M24 12v4m0 0c-2 0-4 1.5-4 4m4-4c2 0 4 1.5 4 4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: 'Développe la coordination et l’équilibre',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <circle cx="24" cy="8" r="4" stroke="currentColor" strokeWidth="2.5" />
        <path
          d="M24 14v8m-6 4l6-4 6 4m-12 0l-4 10m12-10l4 10"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 38c0-2 2-4 4-4m16 4c0-2-2-4-4-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    title: 'Renforce la concentration et l’attention',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="24" cy="24" r="12" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" />
        <circle cx="24" cy="24" r="6" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="24" cy="24" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    title: 'Stimule la confiance en soi par le jeu',
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path
          d="M24 4l5.09 10.31L40 16.07l-8 7.8 1.89 11.01L24 29.76l-9.89 5.12L16 23.87l-8-7.8 10.91-1.76L24 4z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path
          d="M18 32c-1.5 2-2 4.5 0 6s4 .5 6-1c2 1.5 4 2.5 6 1s1.5-4 0-6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

const phases: Phase[] = [
  {
    number: 1,
    title: 'Fondations Motrices',
    weeks: 'Semaines 1-10',
    activities: 8,
    description: 'Développement des bases du mouvement et de l’équilibre',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    ring: 'ring-amber-300',
    text: 'text-amber-700',
  },
  {
    number: 2,
    title: 'Intégration Sensorielle',
    weeks: 'Semaines 11-20',
    activities: 9,
    description: 'Coordination des sens et de la motricité',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50',
    ring: 'ring-orange-300',
    text: 'text-orange-700',
  },
  {
    number: 3,
    title: 'Préparation Cognitive',
    weeks: 'Semaines 21-30',
    activities: 8,
    description: 'Préparation du cerveau aux apprentissages',
    color: 'from-yellow-400 to-amber-500',
    bg: 'bg-yellow-50',
    ring: 'ring-yellow-300',
    text: 'text-yellow-700',
  },
];

const scienceCards: ScienceCard[] = [
  {
    text: 'Le mouvement stimule la création de connexions neuronales essentielles aux apprentissages',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M16 4c-4 0-8 3-8 8 0 3 1.5 5.5 4 7v3a1 1 0 001 1h6a1 1 0 001-1v-3c2.5-1.5 4-4 4-7 0-5-4-8-8-8z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M13 26h6M14 28h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M16 10v3m-2.5 0h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    text: 'Les activités sensorimotrices développent les réflexes qui facilitent la lecture et l’écriture',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <path
          d="M5 26V8a2 2 0 012-2h14a2 2 0 012 2v18l-9-4-9 4z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M10 12h8M10 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M25 10h1a2 2 0 012 2v16l-5-2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    text: 'Le jeu structuré renforce les fonctions exécutives : attention, mémoire, contrôle',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" className="w-7 h-7">
        <rect x="3" y="6" width="26" height="20" rx="3" stroke="currentColor" strokeWidth="2" />
        <path d="M3 12h26" stroke="currentColor" strokeWidth="2" />
        <circle cx="8" cy="9" r="1" fill="currentColor" />
        <circle cx="12" cy="9" r="1" fill="currentColor" />
        <circle cx="16" cy="9" r="1" fill="currentColor" />
        <path d="M8 18h6M8 22h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

const faqs: FAQ[] = [
  {
    question: 'Est-ce de l’éducation physique ?',
    answer:
      'Non. Neurogames est un programme de développement neuromoteur spécialement conçu pour préparer le cerveau aux apprentissages. Contrairement au sport, chaque activité cible des connexions neuronales spécifiques.',
  },
  {
    question: 'Est-ce que c’est sans risque ?',
    answer:
      'Absolument. Toutes les activités sont adaptées aux enfants de 3 à 6 ans et supervisées par des éducatrices formées. Le programme suit une progression douce et respecte le rythme de chaque enfant.',
  },
  {
    question: 'Comment ça complète l’apprentissage en classe ?',
    answer:
      'Neurogames prépare le terrain. En développant l’équilibre, la coordination et l’attention, les enfants sont mieux disposés pour apprendre à lire, écrire et calculer.',
  },
  {
    question: 'Mon enfant est timide, est-ce adapté ?',
    answer:
      'Parfaitement. Le programme utilise le jeu comme vecteur. Les activités sont ludiques et non compétitives, ce qui permet à chaque enfant de progresser à son rythme dans un cadre bienveillant.',
  },
];

/* ---------------------------------------------------------------
   Sub-components
--------------------------------------------------------------- */
const FAQItem = ({ question, answer, isOpen, toggle }: FAQItemProps) => (
  <div className="border border-amber-200/60 rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
    <button
      onClick={toggle}
      className="w-full flex items-center justify-between px-6 py-5 text-left group hover:bg-amber-50/50 transition-colors"
    >
      <span className="text-lg font-semibold text-gray-800 pr-4">{question}</span>
      <motion.span
        animate={{ rotate: isOpen ? 45 : 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center text-xl font-light shadow-md"
      >
        +
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-amber-100">
            <p className="pt-4">{answer}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ---------------------------------------------------------------
   Main component
--------------------------------------------------------------- */
const NeurogamesPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  /* Parallax for hero */
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroBgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* ============================================================
          HERO SECTION
      ============================================================ */}
      <section
        ref={heroRef}
        className="relative min-h-[92vh] flex items-center overflow-hidden"
      >
        {/* Background layer with parallax */}
        <motion.div
          style={{ scale: heroBgScale }}
          className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600"
        />

        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large blurred circles */}
          <motion.div
            className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-yellow-400/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-0 -left-24 w-[400px] h-[400px] bg-orange-600/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
          <div
            className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-white/5 rounded-full blur-2xl"
          />

          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
        </div>

        {/* Content */}
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 w-full"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Text */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="text-center lg:text-left"
              >
                {/* Badge */}
                <motion.div variants={itemVariants} className="mb-6">
                  <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide border border-white/20">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    Maternelle 3-6 ans
                  </span>
                </motion.div>

                {/* Heading */}
                <motion.h1
                  variants={itemVariants}
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.08] tracking-tight"
                >
                  Le mouvement au service du{' '}
                  <span className="relative inline-block">
                    <span className="relative z-10">développement</span>
                    <span className="absolute -bottom-1.5 left-0 w-full h-3 bg-white/20 rounded-full -skew-x-3" />
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                  variants={itemVariants}
                  className="text-lg sm:text-xl text-white/90 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light"
                >
                  Un programme neuromoteur basé sur les neurosciences pour les enfants de 3 à 6 ans
                </motion.p>

                {/* Quick stats */}
                <motion.div
                  variants={itemVariants}
                  className="flex flex-wrap justify-center lg:justify-start gap-6 mb-10"
                >
                  {[
                    { value: '30', label: 'semaines' },
                    { value: '25', label: 'activités' },
                    { value: '3', label: 'phases' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-3xl font-bold text-white">{stat.value}</div>
                      <div className="text-sm text-white/70 font-medium">{stat.label}</div>
                    </div>
                  ))}
                </motion.div>

                <motion.div variants={itemVariants}>
                  <a
                    href="#programme"
                    className="inline-flex items-center gap-2 bg-white text-amber-600 font-semibold px-8 py-4 rounded-2xl shadow-lg shadow-orange-700/20 hover:shadow-xl hover:shadow-orange-700/30 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    Découvrir le programme
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </a>
                </motion.div>
              </motion.div>

              {/* Hero icon / visual */}
              <motion.div
                className="hidden lg:flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
              >
                <div className="relative">
                  {/* Outer glow */}
                  <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl scale-125" />
                  {/* Brain+movement icon */}
                  <motion.div
                    className="relative w-72 h-72 flex items-center justify-center"
                    animate={{ y: [0, -12, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <svg viewBox="0 0 200 200" fill="none" className="w-full h-full text-white drop-shadow-2xl">
                      {/* Brain outline left */}
                      <path
                        d="M100 40c-8-12-24-18-36-14s-22 16-24 30c-6 1-14 8-14 20s6 18 14 22c-2 8 2 18 10 24s18 6 24 2l6 10c4 6 12 8 20 8"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.9"
                      />
                      {/* Brain outline right */}
                      <path
                        d="M100 40c8-12 24-18 36-14s22 16 24 30c6 1 14 8 14 20s-6 18-14 22c2 8-2 18-10 24s-18 6-24 2l-6 10c-4 6-12 8-20 8"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.9"
                      />
                      {/* Neural connections */}
                      <circle cx="80" cy="70" r="4" fill="currentColor" opacity="0.6" />
                      <circle cx="120" cy="70" r="4" fill="currentColor" opacity="0.6" />
                      <circle cx="70" cy="100" r="3" fill="currentColor" opacity="0.5" />
                      <circle cx="130" cy="100" r="3" fill="currentColor" opacity="0.5" />
                      <circle cx="90" cy="90" r="3.5" fill="currentColor" opacity="0.5" />
                      <circle cx="110" cy="90" r="3.5" fill="currentColor" opacity="0.5" />
                      <line x1="80" y1="70" x2="90" y2="90" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
                      <line x1="120" y1="70" x2="110" y2="90" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
                      <line x1="90" y1="90" x2="110" y2="90" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
                      <line x1="70" y1="100" x2="90" y2="90" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
                      <line x1="130" y1="100" x2="110" y2="90" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
                      {/* Movement arcs around brain */}
                      <path
                        d="M50 140c-6 10-2 22 8 28"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        opacity="0.4"
                      />
                      <path
                        d="M150 140c6 10 2 22-8 28"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        opacity="0.4"
                      />
                      <path
                        d="M40 120c-8 4-10 14-4 22"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        opacity="0.3"
                      />
                      <path
                        d="M160 120c8 4 10 14 4 22"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        opacity="0.3"
                      />
                    </svg>

                    {/* Floating particles */}
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-white/30 rounded-full"
                        style={{
                          top: `${20 + i * 15}%`,
                          left: `${10 + i * 18}%`,
                        }}
                        animate={{
                          y: [0, -20, 0],
                          opacity: [0.2, 0.6, 0.2],
                        }}
                        transition={{
                          duration: 3 + i,
                          repeat: Infinity,
                          ease: 'easeInOut',
                          delay: i * 0.5,
                        }}
                      />
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Bottom curve */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <svg viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none" className="w-full h-auto block">
            <path
              d="M0 40C240 80 480 100 720 80C960 60 1200 40 1440 60V100H0V40Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* ============================================================
          BENEFITS SECTION
      ============================================================ */}
      <section className="py-20 sm:py-28 bg-white" id="programme">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-amber-600 font-semibold text-sm tracking-widest uppercase mb-3">
              Pourquoi Neurogames
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Les bénéfices pour votre enfant
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              Un programme complet qui stimule le développement global de l'enfant à travers le mouvement
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -6, transition: { duration: 0.3 } }}
                className="group relative bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-100/80 shadow-sm hover:shadow-xl hover:shadow-amber-200/40 transition-shadow duration-300"
              >
                {/* Icon container */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center mb-6 shadow-lg shadow-amber-300/30 group-hover:scale-110 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-800 leading-snug">{benefit.title}</h3>
                {/* Decorative corner */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-amber-200/50 rounded-tr-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          PROGRAM OVERVIEW / TIMELINE
      ============================================================ */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Decorative bg */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-100/30 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-amber-600 font-semibold text-sm tracking-widest uppercase mb-3">
              Le parcours
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              3 phases progressives
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              Un programme structuré sur 30 semaines, de la fondation motrice à la préparation cognitive
            </p>
          </motion.div>

          {/* Timeline */}
          <div className="max-w-4xl mx-auto relative">
            {/* Vertical connection line (desktop) */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 -translate-x-1/2">
              <div className="w-full h-full bg-gradient-to-b from-amber-300 via-orange-400 to-yellow-400 rounded-full" />
            </div>

            {/* Mobile vertical line */}
            <div className="md:hidden absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-300 via-orange-400 to-yellow-400 rounded-full" />

            <div className="space-y-12 md:space-y-16">
              {phases.map((phase, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-60px' }}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12 ${
                    i % 2 === 1 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Number badge (timeline node) */}
                  <div className="absolute left-5 md:left-1/2 md:-translate-x-1/2 z-10">
                    <div
                      className={`w-10 h-10 md:w-14 md:h-14 rounded-full bg-gradient-to-br ${phase.color} text-white flex items-center justify-center font-bold text-lg md:text-xl shadow-lg ring-4 ring-white`}
                    >
                      {phase.number}
                    </div>
                  </div>

                  {/* Spacer for mobile left alignment */}
                  <div className="md:hidden w-16 flex-shrink-0" />

                  {/* Card */}
                  <div className={`flex-1 ${i % 2 === 1 ? 'md:text-right' : ''}`}>
                    <motion.div
                      whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                      className={`${phase.bg} rounded-3xl p-8 border border-white shadow-lg hover:shadow-xl transition-shadow duration-300 relative overflow-hidden`}
                    >
                      {/* Decorative bg circle */}
                      <div
                        className={`absolute -top-8 ${
                          i % 2 === 1 ? '-left-8' : '-right-8'
                        } w-32 h-32 rounded-full bg-gradient-to-br ${phase.color} opacity-10`}
                      />

                      <div className="relative z-10">
                        <h3 className={`text-xl sm:text-2xl font-bold text-gray-900 mb-2`}>
                          {phase.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${phase.bg} ${phase.text} ring-1 ${phase.ring}`}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {phase.weeks}
                          </span>
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${phase.bg} ${phase.text} ring-1 ${phase.ring}`}>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            {phase.activities} activités
                          </span>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{phase.description}</p>
                      </div>
                    </motion.div>
                  </div>

                  {/* Empty spacer for alternating layout */}
                  <div className="hidden md:block flex-1" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SCIENCE SECTION
      ============================================================ */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-amber-600 font-semibold text-sm tracking-widest uppercase mb-3">
              Fondé sur la recherche
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Approche scientifique
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              Chaque activité est conçue en s'appuyant sur les avancées des neurosciences du développement
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {scienceCards.map((card, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-3xl p-8 border border-amber-100/60 shadow-sm hover:shadow-lg hover:shadow-amber-100/60 transition-all duration-300"
              >
                {/* Top decorative bar */}
                <div className="absolute top-0 left-8 right-8 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 rounded-b-full" />

                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400/20 to-orange-400/20 text-amber-600 flex items-center justify-center mb-6">
                  {card.icon}
                </div>
                <p className="text-gray-700 leading-relaxed font-medium">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ SECTION
      ============================================================ */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-amber-600 font-semibold text-sm tracking-widest uppercase mb-3">
              Questions fréquentes
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Les parents demandent
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <FAQItem
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFAQ === i}
                  toggle={() => setOpenFAQ(openFAQ === i ? null : i)}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          CTA SECTION
      ============================================================ */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-dark to-blue-900" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Découvrez le Groupe Scolaire Sanaa
            </h2>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              Offrez à votre enfant un environnement où le mouvement et l'apprentissage se rejoignent
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-white text-accent font-semibold px-10 py-4 rounded-2xl shadow-lg shadow-blue-900/20 hover:shadow-xl hover:shadow-blue-900/30 hover:-translate-y-0.5 transition-all duration-300 text-lg"
            >
              Nous contacter
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default NeurogamesPage;
